<template>
    <div>
        <div class="d-flex">
            <div>
                <button
                    @click="goToToday"
                    class="btn btn-primary"
                >
                    Today
                </button>
                <button
                    @click="goToPrev"
                    class="btn btn-info"
                >
                    Prev
                </button>
                <button
                    @click="goToNext"
                    class="btn btn-info"
                >
                    Next
                </button>
                <h2 class="d-inline-block ml-2 align-middle">
                    {{ viewName }}
                </h2>
            </div>
            <div class="ml-auto">
                <label>
                    <input
                        type="checkbox"
                        v-model="showJobs"
                        class="d-inline-block align-middle"
                    >
                    <span>Show Jobs</span>
                </label>
            </div>
        </div>
        <table class="calendar table bg-white">
            <thead>
                <tr>
                    <th
                        v-for="day in columns"
                        :key="day.number"
                        class="calendar-head-cell"
                    >
                        {{ day.label }}
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(row, rowIndex) in grid"
                    :key="`${rowIndex}-row`"
                >
                    <td
                        v-for="(d, colIndex) in row"
                        :key="`${rowIndex}-${colIndex}-col`"
                        :class="['calendar-cell', d.month() !== date.month() ? 'calendar-cell-inactive' : '']"
                        @click="$emit('dayClick', d)"
                    >
                        {{ formatDate(d) }}
                        <i
                            class="today"
                            v-if="d.isSame(today, 'day')"
                        />
                        <div
                            v-for="event in eventsForDay(d)"
                            :key="`${event.id}-event`"
                            :class="eventClasses(event)"
                            @click.stop="event.type !== 'timeslot' ? $emit('eventClick', event) : undefined"
                            :style="{
                                'background-color': event.backgroundColor,
                            }"
                        >
                            {{ event.title }}
                            <div class="font-italic">
                                {{ event.content }}
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
import moment from 'moment';
import CalendarEventsService from "../services/CalendarEventsService";
import _ from 'lodash';
import TimeSlotsService from "../services/TimeSlotsService";
import NProgress from "nprogress";
import Auth from "../services/AuthService";

export default {
    name: "CalendarComponent",
    props: {
        initialDate: {
            type: String,
            default: null,
        },
        focusEventId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            view: 'month',
            date: this.initialDate && moment(this.initialDate).isValid()
                ? moment(this.initialDate)
                : moment(),
            showJobs: false,
            eventsLoading: false,
            jobsLoading: false,
            events: [],
            jobs: [],
        };
    },
    computed: {
        today() {
            return moment();
        },
        startDate() {
            return this.date.clone().startOf(this.view).startOf('week');
        },
        endDate() {
            return this.date.clone().endOf(this.view).endOf('week');
        },
        dates() {
            let date = this.startDate.clone();
            const dates = [];
            while (date.isBefore(this.endDate)) {
                dates.push(date.clone());
                date.add(1, 'day');
            }

            return dates;
        },
        columns() {
            return [0, 1, 2, 3, 4, 5, 6].map(d => ({
                number: d,
                label: moment().startOf('week').weekday(d).format('ddd'),
            }));
        },
        grid() {
            const grid = [];
            const colsCount = this.columns.length;
            const rowsCount = this.dates.length / colsCount;

            for (let i = 0; i < rowsCount; i++) {
                const start = (i * colsCount);
                const end = start + colsCount;

                grid.push(this.dates.slice(start, end));
            }

            return grid;
        },
        viewName() {
            return this.date.format('MMMM YYYY')
        },
        loading() {
            return this.jobsLoading || this.eventsLoading;
        },
        canSeePendingHolidays () {
            return Auth.user && Auth.user.admin_access;
        },
    },
    watch: {
        viewName: {
            immediate: true,
            handler() {
                this.refresh();
            },
        },
        showJobs: {
            immediate: true,
            handler() {
                if (this.showJobs) {
                    this.loadJobs();
                }
            },
        },
        loading: {
            immediate: true,
            handler () {
                if (this.loading) {
                    NProgress.start();
                    NProgress.set(.7);
                    NProgress.inc();
                } else {
                    NProgress.done();
                }
            },
        },
        initialDate (value) {
            const nextDate = this.dateFromParam(value);
            if (!nextDate.isSame(this.date, 'day')) {
                this.date = nextDate;
            }
        },
    },
    methods: {
        dateFromParam (value) {
            if (value && moment(value).isValid()) {
                return moment(value);
            }

            return moment();
        },
        eventClasses (event) {
            const type = event.type ? event.type.replace(' ', '-').toLowerCase() : '';
            const classes = ['event', `event-${type}`];
            if (event.type === 'Annual Leave' && event.status === 'Pending') {
                classes.push('event-pending');
            }
            if (this.focusEventId && Number(event.id) === Number(this.focusEventId)) {
                classes.push('event-focused');
            }

            return classes;
        },
        formatDate(date) {
            if (date.date() === 1) {
                return date.format('Do MMM')
            }
            return date.format('Do');
        },
        goToToday() {
            this.date = moment();
            this.$emit('dateChange', this.date);
        },
        goToNext() {
            this.date = this.date.add(1, this.view).clone();
            this.$emit('dateChange', this.date);
        },
        goToPrev() {
            this.date = this.date.subtract(1, this.view).clone();
            this.$emit('dateChange', this.date);
        },
        refresh() {
            this.loadEvents();
            if (this.showJobs) {
                this.loadJobs();
            }
        },
        async loadEvents() {
            this.eventsLoading = true;
            const {data} = await CalendarEventsService.fetchEvents({
                'start': this.startDate.format('YYYY-MM-DD HH:mm:ss'),
                'end': this.endDate.format('YYYY-MM-DD HH:mm:ss'),
                calendar: 1,
            });

            this.events = _.uniqBy(data.map(event => {
                let title = event.type;
                if (event.user) {
                    title = `${title} (${event.user.name})`;
                }
                return {
                    ...event,
                    start: moment(event.start_at),
                    end: moment(event.end_at),
                    title: title,
                };
            }), 'id').filter(e => {
                if (e.type === 'Full Day') {
                    return false;
                }
                if (e.type === 'Annual Leave' && e.status === 'Pending' && !this.canSeePendingHolidays) {
                    return false;
                }

                return true;
            });
            this.eventsLoading = false;
            this.emitFocusedEvent();
        },
        emitFocusedEvent () {
            if (!this.focusEventId) {
                return;
            }
            const match = this.events.find(event => Number(event.id) === Number(this.focusEventId));
            if (match) {
                this.$emit('eventClick', match);
            }
        },
        async loadJobs() {
            this.jobsLoading = true;
            const {data} = await TimeSlotsService.search({
                'start': this.startDate.format('YYYY-MM-DD HH:mm:ss'),
                'end': this.endDate.format('YYYY-MM-DD HH:mm:ss'),
                include: [
                    "job",
                    "user",
                    "job.customer",
                    "job.site",
                    "job.site.address",
                    "job.timeSlots",
                    "job.timeSlots.user"
                ]
            });

            this.jobs = _.uniqBy(data.map(event => {
                let title = event.job.customer.company_name;
                if (event.job.site && event.job.site.address.postcode) {
                    title += ', ' + event.job.site.address.postcode;
                }
                const start = moment(event.start);
                const end = moment(event.end);

                let content = '';
                if (event.user) {
                    content = `${event.user.name} `;
                }
                content += `${start.format('HH:mma')} - ${end.format('HH:mma')}`

                return {
                    ...event,
                    start: start,
                    end: end,
                    title: title,
                    backgroundColor: event.job.color,
                    content: content,
                    type: 'timeslot'
                };
            }), 'id');
            this.jobsLoading = false;
        },
        eventsForDay(day) {
            const start = day.clone().startOf('day');
            const end = day.clone().endOf('day');

            const allEvents = [
                ...this.events,
                ...(this.showJobs ? this.jobs : []),
            ];

            return _.orderBy(allEvents.filter(event => {
                if (event.start.isSameOrBefore(start) && event.end.isSameOrAfter(end)) return true;
                if (event.start.isSameOrAfter(start) && event.start.isSameOrBefore(end)) return true;
                if (event.end.isSameOrAfter(start) && event.end.isSameOrBefore(end)) return true;

                return false;
            }), 'start_at');
        }
    },
}
</script>

<style scoped>
.calendar {
    border-spacing: 0;
    margin-top: 1rem;
}

.calendar-head-cell {
    text-align: center;
    width: calc(100% / 7);
}

.calendar-cell {
    text-align: center;
    height: 10rem;
    border: 1px solid #d7d7d7;
    padding: 2px;
}

.calendar-cell-inactive {
    background: #e3e3e3;
    color: #868686;
}

.event {
    display: block;
    margin: 1px;
    background: #777777;
    border-radius: 3px;
    color: white;
    cursor: pointer;
}

.event-timeslot {
    cursor: inherit;
}

.event-bank-holiday, .event-company-close {
    background: #1e7e34;
}

.event-annual-leave {
    background: #0f6674;
}

.event-pending {
    opacity: 0.55;
    outline: 1px dashed #ffffff;
    outline-offset: -1px;
}

.event-focused {
    box-shadow: 0 0 0 2px #1d7fee;
}

.event-sickness {
    background: #b6a046;
}

.today {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 100%;
    background: #1d7fee;
    border: 1px solid white;
    vertical-align: middle;
    margin-bottom: 1px;
}
</style>


// WEBPACK FOOTER //
// resources/assets/js/components/CalendarComponent.vue